import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Box, Typography, IconButton, Chip, Button,
  alpha, CircularProgress, Tooltip, Divider, Badge,
  TextField, FormControl, InputLabel, Select, MenuItem, Paper,
} from '@mui/material';
import {
  Close as CloseIcon, Add as AddIcon, BugReport as BugIcon,
  NewReleases as FeatureIcon, Upgrade as UpgradeIcon,
  DoneAll as MarkReadIcon, Edit as EditIcon, Delete as DeleteIcon,
  CloudUpload as UploadIcon, Image as ImageIcon,
  FormatBold as BoldIcon, FormatItalic as ItalicIcon,
  FormatListBulleted as BulletIcon, FormatListNumbered as NumberedIcon,
  Title as HeadingIcon,
} from '@mui/icons-material';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { useToast } from '../contexts/ToastContext';
import { changelogApi } from '../services/api';
import { extractApiError } from '../types/apiError';
import ConfirmDialog from './shared/ConfirmDialog';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { HubThemeScope, FONT_DISPLAY } from './clients/hub/designSystem';
import DOMPurify from 'dompurify';
import { useAuth } from '../contexts/AuthContext';

// R4.2 Part C: GOLD constant retired. All in-component usages migrated to
// brand tokens — primary actions go indigo, the title icon inherits
// text.secondary. The constant was local to this file; no external imports.

const ENTRY_TYPES = [
  { key: 'bug_fix', label: 'Bug Fixes', icon: <BugIcon sx={{ fontSize: 16 }} /> },
  { key: 'new_feature', label: 'New Features', icon: <FeatureIcon sx={{ fontSize: 16 }} /> },
  { key: 'major_update', label: 'Major Updates', icon: <UpgradeIcon sx={{ fontSize: 16 }} /> },
];

const CATEGORIES = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'operations', label: 'Operations' },
  { key: 'sales', label: 'Sales' },
  { key: 'finance', label: 'Finance' },
  { key: 'inbox', label: 'Inbox' },
  { key: 'time_tracking', label: 'Time Tracking' },
  { key: 'scorecards', label: 'Scorecards' },
  { key: 'engineering', label: 'Engineering' },
  { key: 'eos', label: 'EOS' },
  { key: 'other', label: 'Other' },
];

// Reskin (Wave 4c): type colors route to theme status tokens (no raw hex —
// the ratchet bans it in .tsx). Categories are shown in the left rail and use
// the neutral/indigo grammar, so a per-category hue map is no longer needed.
const TYPE_TOKEN: Record<string, string> = {
  bug_fix: 'var(--status-off-track)',
  new_feature: 'var(--status-on-track)',
  major_update: 'var(--paprika)',
};

interface WhatsNewDialogProps {
  open: boolean;
  onClose: () => void;
  onUnreadChange?: (count: number) => void;
}

const WhatsNewDialog: React.FC<WhatsNewDialogProps> = ({ open, onClose, onUnreadChange }) => {
  const toast = useToast();
  // Permission-gated = ABSENT, not disabled (THE LOOK §6). The API now requires
  // action:manage_changelog for every write, so showing these to everyone would
  // be offering buttons that 403.
  const { hasAction } = useAuth();
  const canAuthor = hasAction('manage_changelog');
  const [entries, setEntries] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [editEntry, setEditEntry] = useState<any | null>(null);
  const [createMode, setCreateMode] = useState(false);
  const { dialogProps, showConfirm } = useConfirmDialog();

  const typeFilter = activeTab === 0 ? '' : ENTRY_TYPES[activeTab - 1]?.key || '';

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      // Reskin: load the whole (type-filtered) set once; category is applied
      // client-side so the left rail can show per-category unread counts.
      const params: Record<string, any> = { limit: 100 };
      if (typeFilter) params.type = typeFilter;
      const res = await changelogApi.getEntries(params);
      if (res.success) {
        setEntries(res.data || []);
        setTotal(res.total || 0);
      }
    } catch { toast.error('Failed to load updates'); }
    setLoading(false);
  }, [typeFilter, toast]);

  const visibleEntries = useMemo(() => entries.filter(e => e.title && e.title.trim()), [entries]);
  // Per-category unread counts for the left rail (from each entry's is_read).
  const unreadByCategory = useMemo(() => {
    const m: Record<string, number> = {};
    for (const e of visibleEntries) { if (!e.is_read) m[e.category] = (m[e.category] || 0) + 1; }
    return m;
  }, [visibleEntries]);
  const totalUnread = useMemo(() => visibleEntries.reduce((n, e) => n + (e.is_read ? 0 : 1), 0), [visibleEntries]);
  const shownEntries = useMemo(
    () => visibleEntries.filter(e => !categoryFilter || e.category === categoryFilter),
    [visibleEntries, categoryFilter],
  );

  const refreshUnread = useCallback(async () => {
    try {
      const res = await changelogApi.getUnreadCount();
      if (res.success) onUnreadChange?.(res.data.count);
    } catch {}
  }, [onUnreadChange]);

  useEffect(() => { if (open) { loadEntries(); refreshUnread(); } }, [open, loadEntries, refreshUnread]);

  const handleMarkAllRead = async () => {
    try {
      await changelogApi.markAllAsRead();
      toast.success('All marked as read');
      refreshUnread();
    } catch { toast.error('Failed to mark as read'); }
  };

  const handleMarkRead = async (entryId: number) => {
    try {
      await changelogApi.markAsRead(entryId);
      refreshUnread();
    } catch {}
  };

  const handleDelete = (id: number) => {
    showConfirm({
      title: 'Delete entry',
      message: 'Delete this entry?',
      variant: 'danger',
      confirmLabel: 'Delete',
      onConfirm: async () => {
        try {
          await changelogApi.deleteEntry(id);
          setEntries(prev => prev.filter(e => e.id !== id));
          toast.success('Entry deleted');
        } catch { toast.error('Failed to delete'); }
      },
    });
  };

  const handleSaved = () => {
    setEditEntry(null);
    setCreateMode(false);
    loadEntries();
    refreshUnread();
  };

  const fmtDate = (d: string) => {
    try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return d; }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
        PaperProps={{ sx: { minHeight: '80vh', maxHeight: '90vh', borderRadius: '6px', display: 'flex', flexDirection: 'column' } }}>
        <HubThemeScope>
        {/* Header — title + type segment + actions, over the 2px ink rule. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 1.5, borderBottom: '2px solid var(--ink)', flexShrink: 0 }}>
          <Typography sx={{ flex: 1, fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: '20px', color: 'var(--ink)' }}>What's New</Typography>
          <Box sx={{ display: 'inline-flex', gap: '2px', p: '3px', bgcolor: 'var(--paper-2)', borderRadius: '4px' }}>
            {['All', ...ENTRY_TYPES.map(t => t.label)].map((label, i) => (
              <Box key={label} component="button" type="button" onClick={() => setActiveTab(i)}
                sx={{
                  border: 'none', cursor: 'pointer', px: 1.1, py: 0.4, borderRadius: '3px',
                  fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '11px', whiteSpace: 'nowrap',
                  bgcolor: activeTab === i ? 'var(--paper)' : 'transparent',
                  color: activeTab === i ? 'var(--indigo)' : 'var(--ink-soft)',
                  boxShadow: activeTab === i ? '0 1px 2px rgba(0,0,0,0.10)' : 'none',
                }}>{label}</Box>
            ))}
          </Box>
          <Tooltip title="Mark all as read">
            <IconButton size="small" onClick={handleMarkAllRead}><MarkReadIcon fontSize="small" /></IconButton>
          </Tooltip>
          {canAuthor && (
            <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => { setCreateMode(true); setEditEntry(null); }}
              sx={{ textTransform: 'none', borderRadius: '4px', boxShadow: 'none', fontWeight: 700, bgcolor: 'var(--indigo)', '&:hover': { bgcolor: 'var(--indigo)', boxShadow: 'none', filter: 'brightness(0.95)' } }}>
              New Entry
            </Button>
          )}
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </Box>

        {/* Body — left rail of sections (with unread counts) + entries. */}
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Box sx={{ width: 210, flexShrink: 0, borderRight: '1px solid var(--rule)', overflowY: 'auto', py: 1, bgcolor: 'var(--paper-2)' }}>
            {[{ key: '', label: 'All updates', count: totalUnread }, ...CATEGORIES.map(c => ({ key: c.key, label: c.label, count: unreadByCategory[c.key] || 0 }))].map(row => {
              const active = categoryFilter === row.key;
              return (
                <Box key={row.key || 'all'} component="button" type="button" onClick={() => setCategoryFilter(row.key)}
                  sx={{
                    width: '100%', border: 'none', cursor: 'pointer', textAlign: 'left',
                    display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.8,
                    fontFamily: FONT_DISPLAY, fontSize: '13.5px', fontWeight: active ? 700 : 500,
                    bgcolor: active ? 'var(--indigo-soft)' : 'transparent',
                    color: active ? 'var(--indigo-on-soft)' : 'var(--ink-soft)',
                    borderLeft: active ? '2px solid var(--indigo)' : '2px solid transparent',
                    '&:hover': { bgcolor: active ? 'var(--indigo-soft)' : 'var(--rule-soft)' },
                  }}>
                  <span style={{ flex: 1 }}>{row.label}</span>
                  {row.count > 0 && (
                    <Box component="span" sx={{ minWidth: 18, height: 18, px: 0.5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', bgcolor: 'var(--paprika)', color: 'var(--paper)', fontSize: '10.5px', fontWeight: 800 }}>{row.count}</Box>
                  )}
                </Box>
              );
            })}
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
            {loading ? (
              <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
            ) : shownEntries.length === 0 ? (
              <Box py={6} textAlign="center">
                <FeatureIcon sx={{ fontSize: 40, color: 'var(--ink-ghost)', mb: 1 }} />
                <Typography sx={{ color: 'var(--ink-faint)' }}>No updates here yet</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {shownEntries.map(entry => (
                <Paper key={entry.id} variant="outlined" sx={{
                  p: 0, borderRadius: '4px', overflow: 'hidden', flexShrink: 0,
                  border: '1px solid var(--rule)', borderLeft: `3px solid ${TYPE_TOKEN[entry.entry_type] || 'var(--rule-strong)'}`,
                  bgcolor: 'var(--paper)',
                  transition: 'box-shadow 0.12s',
                  '&:hover': { boxShadow: 'var(--lift)' },
                }}>
                  <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {!entry.is_read && <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'var(--indigo)', flexShrink: 0 }} />}
                          <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '14px', lineHeight: 1.3, color: 'var(--ink)' }}>{entry.title}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
                          <Box component="span" sx={{ px: 0.75, py: '1px', borderRadius: '3px', fontSize: '10.5px', fontWeight: 800, letterSpacing: '.02em', color: 'var(--paper)', bgcolor: TYPE_TOKEN[entry.entry_type] || 'var(--ink-faint)' }}>
                            {ENTRY_TYPES.find(t => t.key === entry.entry_type)?.label || entry.entry_type}
                          </Box>
                          <Box component="span" sx={{ px: 0.75, py: '1px', borderRadius: '3px', fontSize: '10.5px', fontWeight: 700, color: 'var(--ink-soft)', border: '1px solid var(--rule)' }}>
                            {CATEGORIES.find(c => c.key === entry.category)?.label || entry.category}
                          </Box>
                          <Typography variant="caption" sx={{ color: 'var(--ink-faint)' }}>{fmtDate(entry.published_at || entry.created_at)}</Typography>
                          {entry.created_by_name && <Typography variant="caption" sx={{ color: 'var(--ink-ghost)' }}>by {entry.created_by_name}</Typography>}
                          {entry.version_tag && (
                            <Box component="span" sx={{ px: 0.5, borderRadius: '3px', fontSize: '10.5px', fontFamily: 'monospace', color: 'var(--ink-faint)', border: '1px solid var(--rule)' }}>{entry.version_tag}</Box>
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.25 }}>
                        {!entry.is_read && (
                          <Tooltip title="Mark as read">
                            <IconButton size="small" onClick={() => handleMarkRead(entry.id)} sx={{ p: 0.5 }}><MarkReadIcon sx={{ fontSize: 16 }} /></IconButton>
                          </Tooltip>
                        )}
                        {canAuthor && (
                          <>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => { setEditEntry(entry); setCreateMode(false); }} sx={{ p: 0.5 }}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={() => handleDelete(entry.id)} sx={{ p: 0.5, '&:hover': { color: 'var(--paprika)' } }}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </Box>

                    {/* DOMPurify here is THE primary XSS control for this
                        surface: the dialog auto-opens on new entries, so an
                        unsanitised body would execute in an admin's session
                        with no click at all. */}
                    {entry.body_html && (
                      <Box sx={{
                        mt: 1, fontSize: '0.875rem', lineHeight: 1.6, color: 'var(--ink-soft)',
                        '& p': { m: 0, mb: 0.5 }, '& ul, & ol': { pl: 2.5, mb: 0.5 },
                        '& h3': { fontSize: '14px', fontWeight: 600, color: 'var(--ink)', mb: 0.5 },
                        '& strong': { color: 'var(--ink)' },
                        '& a': { color: 'var(--indigo-on-soft)' },
                      }}
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(entry.body_html) }}
                      />
                    )}

                    {entry.images && entry.images.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                        {entry.images.map((img: any) => (
                          <Box key={img.id} sx={{ position: 'relative', borderRadius: '4px', overflow: 'hidden', border: '1px solid var(--rule)', cursor: 'pointer', '&:hover': { opacity: 0.9 } }}
                            onClick={() => window.open(changelogApi.imageUrl(img.file_path.split('/').pop() || img.file_name), '_blank')}>
                            <img src={changelogApi.imageUrl(img.file_path.split('/').pop() || img.file_name)} alt={img.caption || img.file_name}
                              style={{ maxWidth: 280, maxHeight: 180, objectFit: 'cover', display: 'block' }} />
                            {img.caption && (
                              <Typography variant="caption" sx={{ display: 'block', px: 1, py: 0.5, bgcolor: 'var(--ink)', color: 'var(--paper)', position: 'absolute', bottom: 0, left: 0, right: 0 }}>{img.caption}</Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Paper>
              ))}
              </Box>
            )}
          </Box>
        </Box>
        </HubThemeScope>
      </Dialog>

      {(createMode || editEntry) && (
        <ChangelogEntryEditor
          open
          entry={editEntry}
          onClose={() => { setEditEntry(null); setCreateMode(false); }}
          onSaved={handleSaved}
        />
      )}

      <ConfirmDialog {...dialogProps} />
    </>
  );
};

// ── Entry Editor Dialog (Create / Edit) ─────────────────────────────

interface EditorProps {
  open: boolean;
  entry: any | null;
  onClose: () => void;
  onSaved: () => void;
}

const ChangelogEntryEditor: React.FC<EditorProps> = ({ open, entry, onClose, onSaved }) => {
  const toast = useToast();
  const [title, setTitle] = useState(entry?.title || '');
  const [entryType, setEntryType] = useState(entry?.entry_type || 'new_feature');
  const [category, setCategory] = useState(entry?.category || 'other');
  const [versionTag, setVersionTag] = useState(entry?.version_tag || '');
  const [saving, setSaving] = useState(false);
  const [images, setImages] = useState<any[]>(entry?.images || []);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({ placeholder: 'Describe what changed, where to find it, and how it helps...' }),
    ],
    content: entry?.body_html || '',
  });

  const handleSave = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const data = {
        title: title.trim(),
        body_html: editor?.getHTML() || '',
        entry_type: entryType,
        category,
        version_tag: versionTag.trim() || undefined,
        published_at: new Date().toISOString(),
      };
      if (entry?.id) {
        await changelogApi.updateEntry(entry.id, data);
        toast.success('Entry updated');
      } else {
        const res = await changelogApi.createEntry(data);
        if (res.success && res.data?.id && pendingFiles.length > 0) {
          for (const f of pendingFiles) {
            await changelogApi.uploadImage(res.data.id, f);
          }
          setPendingFiles([]);
        }
        toast.success('Entry created');
      }
      onSaved();
    } catch (err: any) {
      toast.error(extractApiError(err, 'Failed to save'));
    }
    setSaving(false);
  };

  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  const handleFileUpload = async (fileList: FileList | null) => {
    if (!fileList?.length) return;
    if (entry?.id) {
      setUploading(true);
      try {
        for (let i = 0; i < fileList.length; i++) {
          const res = await changelogApi.uploadImage(entry.id, fileList[i]);
          if (res.success) setImages(prev => [...prev, res.data]);
        }
        toast.success('Image uploaded');
      } catch { toast.error('Upload failed'); }
      setUploading(false);
    } else {
      setPendingFiles(prev => [...prev, ...Array.from(fileList)]);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const f = items[i].getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length > 0) {
      e.preventDefault();
      const dt = new DataTransfer();
      files.forEach(f => dt.items.add(f));
      handleFileUpload(dt.files);
    }
  };

  const handleDeleteImage = async (imgId: number) => {
    try {
      await changelogApi.deleteImage(imgId);
      setImages(prev => prev.filter(i => i.id !== imgId));
    } catch { toast.error('Failed to delete image'); }
  };

  const btnSx = { borderRadius: 1, p: 0.25, minWidth: 0, width: 28, height: 28 };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth onPaste={handlePaste}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        {/* R4.2 Part C: edit/add title icon inherits text.secondary — was GOLD. */}
        {entry ? <EditIcon fontSize="small" sx={{ color: 'text.secondary' }} /> : <AddIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
        <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
          {entry ? 'Edit Entry' : 'Create New Entry'}
        </Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <TextField label="Title" value={title} onChange={e => setTitle(e.target.value)} fullWidth autoFocus
          placeholder="e.g. New project types now available in Operations" />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Type</InputLabel>
            <Select value={entryType} onChange={e => setEntryType(e.target.value)} label="Type">
              {ENTRY_TYPES.map(t => <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Category</InputLabel>
            <Select value={category} onChange={e => setCategory(e.target.value)} label="Category">
              {CATEGORIES.map(c => <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Version Tag" value={versionTag} onChange={e => setVersionTag(e.target.value)}
            size="small" placeholder="v1.2.3" sx={{ flex: 1 }} />
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Description</Typography>
          {editor && (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden', '&:focus-within': { borderColor: 'primary.main' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, px: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
                <Tooltip title="Bold"><IconButton size="small" sx={btnSx} onClick={() => editor.chain().focus().toggleBold().run()}><BoldIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                <Tooltip title="Italic"><IconButton size="small" sx={btnSx} onClick={() => editor.chain().focus().toggleItalic().run()}><ItalicIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
                <Tooltip title="Heading"><IconButton size="small" sx={btnSx} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><HeadingIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                <Tooltip title="Bullet List"><IconButton size="small" sx={btnSx} onClick={() => editor.chain().focus().toggleBulletList().run()}><BulletIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                <Tooltip title="Numbered List"><IconButton size="small" sx={btnSx} onClick={() => editor.chain().focus().toggleOrderedList().run()}><NumberedIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
              </Box>
              <Box sx={{
                px: 1.5, py: 1, minHeight: 120,
                '& .tiptap': {
                  outline: 'none', minHeight: 100, fontSize: '0.875rem',
                  '& p.is-editor-empty:first-of-type::before': { content: 'attr(data-placeholder)', float: 'left', color: 'text.disabled', pointerEvents: 'none', height: 0 },
                  '& h3': { fontSize: '14px', fontWeight: 600 },
                  '& p': { mb: 0.25, lineHeight: 1.5 },
                  '& ul, & ol': { pl: 2.5, mb: 0.5 },
                },
              }}>
                <EditorContent editor={editor} />
              </Box>
            </Box>
          )}
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Screenshots (paste from clipboard or upload)</Typography>
          <Box
            onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
            onDrop={e => { e.preventDefault(); e.stopPropagation(); handleFileUpload(e.dataTransfer.files); }}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              border: '2px dashed', borderColor: 'divider', borderRadius: 1.5, p: 2,
              textAlign: 'center', cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main', bgcolor: (t) => alpha(t.palette.primary.main, 0.02) },
            }}
          >
            <input ref={fileInputRef} type="file" multiple accept="image/*" hidden onChange={e => handleFileUpload(e.target.files)} />
            {uploading ? <CircularProgress size={20} /> : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <UploadIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">Drag & drop, click, or paste (Ctrl+V) screenshots</Typography>
              </Box>
            )}
          </Box>

          {(images.length > 0 || pendingFiles.length > 0) && (
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              {images.map((img: any) => (
                <Box key={img.id} sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                  <img src={changelogApi.imageUrl(img.file_path.split('/').pop() || img.file_name)}
                    alt={img.file_name} style={{ width: 100, height: 70, objectFit: 'cover' }} />
                  <IconButton size="small" onClick={() => handleDeleteImage(img.id)}
                    sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', p: 0.25, '&:hover': { bgcolor: 'error.main' } }}>
                    <DeleteIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ))}
              {pendingFiles.map((f, idx) => (
                <Box key={`pending-${idx}`} sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden', border: '1px dashed', borderColor: 'primary.main', width: 100, height: 70, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha('#000', 0.03) }}>
                  <ImageIcon sx={{ fontSize: 24, color: 'text.disabled' }} />
                  <Typography variant="caption" sx={{ position: 'absolute', bottom: 2, left: 4, right: 4, textAlign: 'center', fontSize: '0.55rem' }} noWrap>{f.name}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 1 }}>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
          {/* R4.2 Part C: Save/Publish CTA migrated off GOLD → indigo primary. */}
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving || !title.trim()}
            sx={{ textTransform: 'none' }}>
            {saving ? <CircularProgress size={18} /> : entry ? 'Save Changes' : 'Publish'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default WhatsNewDialog;
